import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Group,
  Modal,
  Radio,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOrgs } from '../api/orgs';
import {
  useDismissCandidate,
  useMergeCandidates,
  useMergeFromCandidate,
  type PatientMergeCandidate,
  type RankedCandidate,
} from '../api/patient-merge';

/**
 * Admin review queue for patient deduplication. Lists every
 * pending merge candidate for the chosen organisation, with the
 * provisional record beside its ranked match candidates and the
 * features that triggered the flag (DOB match, postal prefix
 * match, name similarity).
 *
 * Two actions per row:
 *   - Merge: opens a confirmation dialog that asks the admin to pick
 *     which existing patient survives, optionally provide a reason,
 *     and warns about the unmerge window.
 *   - Not a duplicate: marks the candidate as dismissed so it stops
 *     showing up in this queue.
 */
export function PatientDuplicatesPage() {
  const [params, setParams] = useSearchParams();
  const { data: orgs } = useOrgs();
  const org = params.get('org') ?? '';
  // No org → cross-org list on first load. The Select can then be used
  // to filter down to a specific org once the admin has eyeballed the
  // platform-wide queue.
  const query = useMergeCandidates(org || undefined);

  // Selected candidate drives the Merge modal. Storing the whole row
  // (rather than just its id) lets the modal render side-by-side
  // comparison data without re-fetching.
  const [active, setActive] = useState<PatientMergeCandidate | null>(null);
  const [mergeOpen, mergeCtl] = useDisclosure(false);
  const [dismissOpen, dismissCtl] = useDisclosure(false);

  function patchOrg(orgId: string) {
    const next = new URLSearchParams(params);
    if (orgId) next.set('org', orgId);
    else next.delete('org');
    setParams(next, { replace: true });
  }

  return (
    <Stack gap="lg">
      <div>
        <Title order={2}>Duplicate users</Title>
        <Text c="dimmed" size="sm">
          Review records the platform thinks may belong to the same person — listed across every
          organisation by default, with the matching org and any products the patient is linked to.
          Use the Organisation filter to narrow the queue. Merging is reversible within 12 months;
          dismissing marks the record as a confirmed distinct individual.
        </Text>
      </div>

      <Group gap="md" align="flex-end" wrap="wrap">
        <Select
          label="Organisation"
          placeholder="All organisations"
          value={org || null}
          onChange={(v) => patchOrg(v ?? '')}
          data={(orgs ?? []).map((o) => ({ value: o.id, label: o.name }))}
          searchable
          clearable
          w={260}
        />
      </Group>

      {query.isPending && (
        <Center mih={240}>
          <Text c="dimmed">Loading…</Text>
        </Center>
      )}

      {!query.isPending && query.data?.items.length === 0 && (
        <Center mih={240}>
          <Text c="dimmed">
            No pending duplicate reviews — every new patient was either a clean match or
            unambiguous.
          </Text>
        </Center>
      )}

      {query.data?.items.map((row) => (
        <Card key={row.id} withBorder padding="md">
          <Stack gap="sm">
            <Group justify="space-between" align="flex-start">
              <div>
                <Group gap="xs" align="center" wrap="wrap">
                  <Text fw={600}>
                    {row.provisional.given_name} {row.provisional.family_name}
                  </Text>
                  {row.organisation_name && (
                    <Badge variant="light" color="gray">
                      {row.organisation_name}
                    </Badge>
                  )}
                  {row.products.map((p) => (
                    <Badge key={p.id} variant="light" color="blue" title={p.display_name}>
                      {p.code}
                    </Badge>
                  ))}
                </Group>
                <Text size="xs" c="dimmed">
                  DOB {row.provisional.dob}
                  {row.provisional.postal_code ? ` · ${row.provisional.postal_code}` : ''} · flagged{' '}
                  {new Date(row.created_at).toLocaleString()}
                </Text>
              </div>
              <Group gap="sm">
                <Button
                  size="compact-sm"
                  onClick={() => {
                    setActive(row);
                    mergeCtl.open();
                  }}
                >
                  Merge
                </Button>
                <Button
                  size="compact-sm"
                  variant="default"
                  onClick={() => {
                    setActive(row);
                    dismissCtl.open();
                  }}
                >
                  Not a duplicate
                </Button>
              </Group>
            </Group>

            <Text size="sm" fw={500}>
              Suggested matches
            </Text>
            <Table withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>DOB</Table.Th>
                  <Table.Th>Why</Table.Th>
                  <Table.Th>Score</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {row.candidates.map((c) => (
                  <Table.Tr key={c.patient_id}>
                    <Table.Td>
                      {c.given_name} {c.family_name}
                    </Table.Td>
                    <Table.Td>{c.dob}</Table.Td>
                    <Table.Td>
                      <MatchReasons candidate={c} />
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={c.score >= 0.8 ? 'green' : 'yellow'}>
                        {Math.round(c.score * 100)}%
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Stack>
        </Card>
      ))}

      <MergeModal
        opened={mergeOpen}
        candidate={active}
        onClose={() => {
          mergeCtl.close();
          setActive(null);
        }}
      />
      <DismissModal
        opened={dismissOpen}
        candidate={active}
        onClose={() => {
          dismissCtl.close();
          setActive(null);
        }}
      />
    </Stack>
  );
}

function MatchReasons({ candidate }: { candidate: RankedCandidate }) {
  const reasons: string[] = [];
  if (candidate.dob_match) reasons.push('DOB');
  if (candidate.postal_prefix_match) reasons.push('postcode prefix');
  if (candidate.name_score >= 0.85) reasons.push('name match');
  else if (candidate.name_score >= 0.6) reasons.push('similar name');
  return (
    <Text size="sm" c="dimmed">
      {reasons.length === 0 ? 'name proximity' : reasons.join(' + ')}
    </Text>
  );
}

function MergeModal({
  opened,
  candidate,
  onClose,
}: {
  opened: boolean;
  candidate: PatientMergeCandidate | null;
  onClose: () => void;
}) {
  const merge = useMergeFromCandidate();
  const [survivingId, setSurvivingId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setSurvivingId('');
    setReason('');
    setError(null);
  }

  function handleSubmit() {
    if (!candidate) return;
    setError(null);
    if (!survivingId) {
      setError('Pick which existing patient the provisional record should be merged into.');
      return;
    }
    merge.mutate(
      {
        candidate_id: candidate.id,
        org_id: candidate.organisation_id,
        surviving_patient_id: survivingId,
        reason: reason.trim() || undefined,
      },
      {
        onSuccess: () => {
          notifications.show({ color: 'green', message: 'Records merged.' });
          reset();
          onClose();
        },
        onError: () => {
          setError('Merge failed. Try again or check the server logs.');
        },
      },
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Merge patient records"
      size="lg"
      centered
    >
      {candidate && (
        <Stack gap="md">
          {error && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              {error}
            </Alert>
          )}
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
            <strong>This is reversible for 12 months.</strong> After the retention window the merge
            becomes permanent and cannot be undone. The provisional record (and any identifiers
            attached to it) move into the surviving patient.
          </Alert>

          <Box>
            <Text size="sm" fw={600}>
              Provisional record (will be absorbed)
            </Text>
            <Text size="sm">
              {candidate.provisional.given_name} {candidate.provisional.family_name} · DOB{' '}
              {candidate.provisional.dob}
            </Text>
          </Box>

          <Radio.Group
            label="Surviving record (keeps its ID + history)"
            value={survivingId}
            onChange={setSurvivingId}
          >
            <Stack gap="xs" mt="xs">
              {candidate.candidates.map((c) => (
                <Radio
                  key={c.patient_id}
                  value={c.patient_id}
                  label={
                    <span>
                      <strong>
                        {c.given_name} {c.family_name}
                      </strong>{' '}
                      · DOB {c.dob} ·{' '}
                      <Anchor size="sm" component="span">
                        {Math.round(c.score * 100)}%
                      </Anchor>
                    </span>
                  }
                />
              ))}
            </Stack>
          </Radio.Group>

          <TextInput
            label="Reason (optional)"
            placeholder="e.g. confirmed via call back"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
          />

          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={onClose} disabled={merge.isPending}>
              Cancel
            </Button>
            <Button color="red" onClick={handleSubmit} loading={merge.isPending}>
              Merge records
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function DismissModal({
  opened,
  candidate,
  onClose,
}: {
  opened: boolean;
  candidate: PatientMergeCandidate | null;
  onClose: () => void;
}) {
  const dismiss = useDismissCandidate();
  const [reason, setReason] = useState('');

  function handleSubmit() {
    if (!candidate) return;
    dismiss.mutate(
      {
        candidate_id: candidate.id,
        org_id: candidate.organisation_id,
        reason: reason.trim() || undefined,
      },
      {
        onSuccess: () => {
          notifications.show({
            color: 'green',
            message: 'Marked as a distinct patient — promoted to active.',
          });
          setReason('');
          onClose();
        },
      },
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={() => {
        setReason('');
        onClose();
      }}
      title="Mark as not a duplicate"
      centered
    >
      {candidate && (
        <Stack gap="md">
          <Text size="sm">
            Confirm that{' '}
            <strong>
              {candidate.provisional.given_name} {candidate.provisional.family_name}
            </strong>{' '}
            is a distinct person from every suggested match. The provisional record will be promoted
            to active status and the candidate stops appearing in this queue.
          </Text>
          <TextInput
            label="Reason (optional)"
            placeholder="e.g. different DOB on chart"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={onClose} disabled={dismiss.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={dismiss.isPending}>
              Confirm — not a duplicate
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
